/**
 * Константы и дефолтные значения для Larte Chat Widget
 */

import { LanguageTranslations, Translations } from '@/types/config.types';

/**
 * Лимиты
 */
export const LIMITS = {
  /** Максимальная длина сообщения (символов) */
  MAX_MESSAGE_LENGTH: 2000,
  
  /** Максимальный размер файла (байт) - 10MB */
  MAX_FILE_SIZE: 10 * 1024 * 1024,
  
  /** Максимальное количество файлов в одном сообщении */
  MAX_FILES_PER_MESSAGE: 1,
  
  /** Количество сообщений в истории для кеширования */
  CACHED_MESSAGES_COUNT: 50,
  
  /** TTL для данных в localStorage (дни) */
  STORAGE_TTL_DAYS: 30,
  
  /** Таймаут для API запросов (мс) */
  API_TIMEOUT: 30000,
  
  /** Количество попыток retry при ошибке */
  MAX_RETRY_ATTEMPTS: 3,
  
  /** Задержка между retry (мс) */
  RETRY_DELAY: 2000,
} as const;

/**
 * Ключи для localStorage
 */
export const STORAGE_KEYS = {
  /** ID пользователя */
  USER_ID: 'larte_chat_user_id',
  
  /** ID текущей беседы */
  CONVERSATION_ID: 'larte_chat_conversation_id',
  
  /** История сообщений */
  MESSAGES_HISTORY: 'larte_chat_messages',
  
  /** Язык виджета */
  LANGUAGE: 'larte_chat_language',
  
  /** Временная метка последнего обновления */
  LAST_UPDATED: 'larte_chat_last_updated',
} as const;

/**
 * Поддерживаемые форматы файлов
 */
export const SUPPORTED_FILE_TYPES = {
  image: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  document: [
    'application/pdf',
    'text/plain',
    'text/markdown',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  audio: ['audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/webm', 'audio/x-m4a'],
  video: ['video/mp4', 'video/quicktime', 'video/mpeg', 'video/webm'],
} as const;

/**
 * Расширения файлов для валидации
 */
export const SUPPORTED_FILE_EXTENSIONS = {
  image: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'],
  document: ['.txt', '.md', '.pdf', '.docx', '.xlsx', '.csv', '.html'],
  audio: ['.mp3', '.m4a', '.wav', '.webm'],
  video: ['.mp4', '.mov', '.mpeg', '.webm'],
} as const;

/**
 * Дефолтные переводы UI для русского языка
 */
const RU_TRANSLATIONS: LanguageTranslations = {
  header_title: 'Larte Assistant',
  status_online: 'Онлайн',
  status_offline: 'Оффлайн',
  input_placeholder: 'Введите сообщение...',
  send_button: 'Отправить',
  new_conversation: 'Новая беседа',
  attach_file: 'Прикрепить файл',
  voice_input: 'Голосовой ввод',
  error_network: 'Ошибка соединения. Проверьте интернет.',
  error_api: 'Ошибка сервера. Попробуйте позже.',
  error_empty_message: 'Сообщение не может быть пустым',
  error_file_too_large: 'Файл слишком большой (макс 10MB)',
  error_unsupported_file: 'Неподдерживаемый формат файла',
  retry: 'Повторить',
  close: 'Закрыть',
  minimize: 'Свернуть',
  contacts: 'Контакты',
  typing: 'Печатает...',
  aria_chat: 'Чат с AI ассистентом',
  aria_typing: 'Печатает...',
  aria_open_image: 'Открыть изображение',
  aria_download: 'Скачать',
  aria_view_image: 'Просмотр изображения',
  aria_prev_image: 'Предыдущее изображение',
  aria_next_image: 'Следующее изображение',
  aria_prev_video: 'Предыдущее видео',
  aria_next_video: 'Следующее видео',
  drag_title: 'Загрузить фото машины',
  drag_hint: 'Отпустите для автоматического определения модели и подбора комплектов LARTE',
  empty_chat: 'Начните беседу',
  image_alt: 'Изображение',
  video_unsupported: 'Ваш браузер не поддерживает воспроизведение видео.',
};

/**
 * Дефолтные переводы UI для английского языка
 */
const EN_TRANSLATIONS: LanguageTranslations = {
  header_title: 'Larte Assistant',
  status_online: 'Online',
  status_offline: 'Offline',
  input_placeholder: 'Type a message...',
  send_button: 'Send',
  new_conversation: 'New conversation',
  attach_file: 'Attach file',
  voice_input: 'Voice input',
  error_network: 'Connection error. Check your internet.',
  error_api: 'Server error. Try again later.',
  error_empty_message: 'Message cannot be empty',
  error_file_too_large: 'File too large (max 10MB)',
  error_unsupported_file: 'Unsupported file format',
  retry: 'Retry',
  close: 'Close',
  minimize: 'Minimize',
  contacts: 'Contacts',
  typing: 'Typing...',
  aria_chat: 'Chat with AI assistant',
  aria_typing: 'Typing...',
  aria_open_image: 'Open image',
  aria_download: 'Download',
  aria_view_image: 'Image viewer',
  aria_prev_image: 'Previous image',
  aria_next_image: 'Next image',
  aria_prev_video: 'Previous video',
  aria_next_video: 'Next video',
  drag_title: 'Upload car photo',
  drag_hint: 'Drop to automatically identify the model and find matching LARTE kits',
  empty_chat: 'Start a conversation',
  image_alt: 'Image',
  video_unsupported: 'Your browser does not support video playback.',
};

/**
 * Дефолтные переводы UI для немецкого языка
 */
const DE_TRANSLATIONS: LanguageTranslations = {
  header_title: 'Larte Assistent',
  status_online: 'Online',
  status_offline: 'Offline',
  input_placeholder: 'Nachricht eingeben...',
  send_button: 'Senden',
  new_conversation: 'Neues Gespräch',
  attach_file: 'Datei anhängen',
  voice_input: 'Spracheingabe',
  error_network: 'Verbindungsfehler. Überprüfen Sie Ihre Internetverbindung.',
  error_api: 'Serverfehler. Bitte versuchen Sie es später erneut.',
  error_empty_message: 'Nachricht darf nicht leer sein',
  error_file_too_large: 'Datei zu groß (max 10MB)',
  error_unsupported_file: 'Nicht unterstütztes Dateiformat',
  retry: 'Wiederholen',
  close: 'Schließen',
  minimize: 'Minimieren',
  contacts: 'Kontakte',
  typing: 'Tippt...',
  aria_chat: 'Chat mit KI-Assistent',
  aria_typing: 'Tippt...',
  aria_open_image: 'Bild öffnen',
  aria_download: 'Herunterladen',
  aria_view_image: 'Bildansicht',
  aria_prev_image: 'Vorheriges Bild',
  aria_next_image: 'Nächstes Bild',
  aria_prev_video: 'Vorheriges Video',
  aria_next_video: 'Nächstes Video',
  drag_title: 'Autofoto hochladen',
  drag_hint: 'Loslassen, um das Modell automatisch zu erkennen und passende LARTE-Kits zu finden',
  empty_chat: 'Gespräch beginnen',
  image_alt: 'Bild',
  video_unsupported: 'Ihr Browser unterstützt keine Videowiedergabe.',
};

/**
 * Дефолтные переводы UI для французского языка
 */
const FR_TRANSLATIONS: LanguageTranslations = {
  header_title: 'Assistant Larte',
  status_online: 'En ligne',
  status_offline: 'Hors ligne',
  input_placeholder: 'Tapez un message...',
  send_button: 'Envoyer',
  new_conversation: 'Nouvelle conversation',
  attach_file: 'Joindre un fichier',
  voice_input: 'Saisie vocale',
  error_network: 'Erreur de connexion. Vérifiez votre internet.',
  error_api: 'Erreur serveur. Réessayez plus tard.',
  error_empty_message: 'Le message ne peut pas être vide',
  error_file_too_large: 'Fichier trop volumineux (max 10MB)',
  error_unsupported_file: 'Format de fichier non pris en charge',
  retry: 'Réessayer',
  close: 'Fermer',
  minimize: 'Réduire',
  contacts: 'Contacts',
  typing: 'Tape...',
  aria_chat: "Chat avec l'assistant IA",
  aria_typing: 'Tape...',
  aria_open_image: "Ouvrir l'image",
  aria_download: 'Télécharger',
  aria_view_image: "Visionneuse d'images",
  aria_prev_image: 'Image précédente',
  aria_next_image: 'Image suivante',
  aria_prev_video: 'Vidéo précédente',
  aria_next_video: 'Vidéo suivante',
  drag_title: 'Télécharger la photo du véhicule',
  drag_hint: 'Déposez pour identifier automatiquement le modèle et trouver les kits LARTE adaptés',
  empty_chat: 'Commencez une conversation',
  image_alt: 'Image',
  video_unsupported: 'Votre navigateur ne prend pas en charge la lecture vidéo.',
};

/**
 * Все дефолтные переводы
 */
export const DEFAULT_TRANSLATIONS: Translations = {
  ru: RU_TRANSLATIONS,
  en: EN_TRANSLATIONS,
  de: DE_TRANSLATIONS,
  fr: FR_TRANSLATIONS,
};

/**
 * Информация о контактах (для dropdown)
 */
export const CONTACTS = {
  phone: '+7 (495) 999-83-83',
  phoneLink: 'tel:+74959998383',
  whatsapp: 'https://wa.me/74959998383',
  telegram: 'https://t.me/lartedesign',
  email: 'info@larte-design.ru',
  emailLink: 'mailto:info@larte-design.ru',
  website: 'https://larte-design.ru',
} as const;

/**
 * Ping interval для SSE (мс)
 */
export const SSE_PING_INTERVAL = 10000; // 10 секунд

